import type {
  DbExposureCheck,
  InventoryPackage,
  InventoryPort,
  InventoryProcess,
  PortReach,
  ServerInventory,
} from '@bartizo/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useDeferredValue, useState, type ReactNode } from 'react';
import { getServerInventory, listServerPackages } from '../api/servers.js';
import { formatBytes } from '../lib/format.js';
import { formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Card } from '../ui/Card.js';
import { DataTable, Facts, type Column } from '../ui/DataTable.js';
import { TextField } from '../ui/Field.js';

/**
 * What is on a server, as its Wazuh agent's syscollector found it and the
 * central manager holds it (P11.8.7): the system, what listens and who can reach
 * it — a public database port checked against `db_exposure` — the installed
 * packages and the running processes. The agent scans hourly; the API reads each
 * server about as often.
 */

const REACH: Readonly<Record<PortReach, { label: string; badge: string }>> = {
  public: { label: 'Internet', badge: 'badge-warning' },
  private: { label: 'Private network', badge: 'badge-muted' },
  loopback: { label: 'This server only', badge: 'badge-muted' },
};

const DB_CHECK: Readonly<Record<DbExposureCheck, { label: string; badge: string; title: string }>> =
  {
    reported: {
      label: 'db_exposure reported it',
      badge: 'badge-muted',
      title: "The agent's database exposure check has said so in the last day.",
    },
    not_reported: {
      label: 'db_exposure has not said',
      badge: 'badge-danger',
      title:
        "The agent's database exposure check is on here but has not reported this port: check what it sees.",
    },
    off: {
      label: 'db_exposure is off',
      badge: 'badge-danger',
      title: 'Database exposure checks are off on this server; the database role turns them on.',
    },
  };

export function InventoryTab({
  serverId,
  timezone,
}: {
  serverId: string;
  timezone: string;
}): ReactNode {
  const inventory = useQuery({
    queryKey: ['inventory', serverId],
    queryFn: () => getServerInventory(serverId),
    refetchInterval: 5 * 60_000,
  });

  if (inventory.isPending) {
    return <div className="skeleton skeleton-block" aria-busy="true" aria-label="Loading" />;
  }
  if (inventory.isError) {
    return (
      <Alert tone="danger" title="Could not load the inventory">
        {inventory.error instanceof Error ? inventory.error.message : 'Try again in a moment.'}
      </Alert>
    );
  }
  const data = inventory.data;
  if (data.system === null) {
    return (
      <Card title="Inventory">
        <p className="muted text-sm">
          Nothing read yet. The central server reads what this server's Wazuh agent found — its
          system, installed packages, listening ports and processes — about once an hour once the
          agent reports to it. A server whose Wazuh runs locally has none here.
        </p>
      </Card>
    );
  }
  return (
    <>
      <SystemCard system={data.system} data={data} timezone={timezone} />
      <PortsCard ports={data.ports} scannedAt={data.scannedAt.ports} timezone={timezone} />
      <PackagesCard serverId={serverId} data={data} timezone={timezone} />
      <ProcessesCard processes={data.processes} scannedAt={data.scannedAt.processes} />
    </>
  );
}

function SystemCard({
  system,
  data,
  timezone,
}: {
  system: NonNullable<ServerInventory['system']>;
  data: ServerInventory;
  timezone: string;
}): ReactNode {
  const unknown = <span className="muted">unknown</span>;
  return (
    <Card
      title="System"
      description={
        data.readAt === null
          ? 'Not read yet.'
          : `Read ${formatRelative(data.readAt)} (${formatInstant(data.readAt, { timezone })}).`
      }
    >
      <Facts
        items={[
          [
            'Operating system',
            system.osName === null
              ? unknown
              : [system.osName, system.osVersion].filter((each) => each !== null).join(' '),
          ],
          ['Kernel', system.kernelRelease ?? unknown],
          ['Architecture', system.architecture ?? unknown],
          ['Host name', system.hostname ?? unknown],
          [
            'Processor',
            system.cpuName === null
              ? unknown
              : `${system.cpuName}${system.cpuCores === null ? '' : `, ${String(system.cpuCores)} cores`}`,
          ],
          ['Memory', system.ramTotalBytes === null ? unknown : formatBytes(system.ramTotalBytes)],
          [
            'Addresses',
            system.addresses.length === 0
              ? unknown
              : system.addresses.map((each) => `${each.address} (${each.iface})`).join(', '),
          ],
        ]}
      />
    </Card>
  );
}

function PortsCard({
  ports,
  scannedAt,
  timezone,
}: {
  ports: readonly InventoryPort[];
  scannedAt: string | null;
  timezone: string;
}): ReactNode {
  const reachable = ports.filter((port) => port.reach === 'public').length;
  const columns: Column<InventoryPort>[] = [
    {
      key: 'port',
      header: 'Port',
      rowHeader: true,
      cell: (row) => (
        <>
          <span className="mono">
            {row.protocol.replace(/6$/, '')}/{row.port}
          </span>
          {row.database !== null && <span className="cell-sub">{row.database}</span>}
        </>
      ),
      sortValue: (row) => row.port,
      align: 'end',
    },
    {
      key: 'address',
      header: 'Listens on',
      cell: (row) => <span className="mono">{row.address}</span>,
      sortValue: (row) => row.address,
    },
    {
      key: 'reach',
      header: 'Reachable from',
      cell: (row) => (
        <>
          <span className={`badge ${REACH[row.reach].badge}`}>{REACH[row.reach].label}</span>
          {row.dbExposure !== null && (
            <span
              className={`badge ${DB_CHECK[row.dbExposure].badge}`}
              title={DB_CHECK[row.dbExposure].title}
            >
              {DB_CHECK[row.dbExposure].label}
            </span>
          )}
        </>
      ),
      sortValue: (row) => ['public', 'private', 'loopback'].indexOf(row.reach),
    },
    {
      key: 'process',
      header: 'Process',
      cell: (row) =>
        row.process === null && row.command === null ? (
          <span className="muted">unknown</span>
        ) : (
          <>
            {row.process ?? row.command}
            {row.command !== null && row.command !== row.process && (
              <span className="cell-sub mono">{row.command}</span>
            )}
          </>
        ),
      sortValue: (row) => row.process ?? row.command ?? '',
    },
    {
      key: 'since',
      header: 'Seen since',
      wideOnly: true,
      cell: (row) => (
        <span title={formatInstant(row.firstSeenAt, { timezone })}>
          {formatRelative(row.firstSeenAt)}
        </span>
      ),
      sortValue: (row) => row.firstSeenAt,
    },
  ];
  return (
    <Card
      title="Listening ports"
      description={`What accepts connections, and from where — judged by this server's own addresses; a firewall or a cloud security group may close more.${scannedAt === null ? '' : ` Last change ${formatRelative(scannedAt)}.`}`}
      aside={
        <span className={`badge ${reachable > 0 ? 'badge-warning' : 'badge-success'}`}>
          {reachable === 1
            ? '1 reachable from the internet'
            : `${String(reachable)} reachable from the internet`}
        </span>
      }
    >
      <DataTable
        caption="Listening ports"
        columns={columns}
        rows={ports}
        rowKey={(row) => `${row.protocol}|${row.address}|${String(row.port)}`}
        empty={<p className="muted text-sm">Nothing listens.</p>}
      />
    </Card>
  );
}

function PackagesCard({
  serverId,
  data,
  timezone,
}: {
  serverId: string;
  data: ServerInventory;
  timezone: string;
}): ReactNode {
  const [search, setSearch] = useState('');
  const q = useDeferredValue(search.trim());
  const list = useInfiniteQuery({
    queryKey: ['inventory-packages', serverId, q],
    queryFn: ({ pageParam }) =>
      listServerPackages(serverId, {
        q,
        ...(pageParam === undefined ? {} : { cursor: pageParam }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const columns: Column<InventoryPackage>[] = [
    {
      key: 'name',
      header: 'Package',
      rowHeader: true,
      cell: (row) => (
        <>
          {row.name}
          {row.source !== null && <span className="cell-sub">from {row.source}</span>}
        </>
      ),
      sortValue: (row) => row.name,
    },
    {
      key: 'version',
      header: 'Version',
      cell: (row) => <span className="mono">{row.version}</span>,
    },
    {
      key: 'arch',
      header: 'Architecture',
      wideOnly: true,
      cell: (row) => `${row.architecture}${row.format === '' ? '' : ` · ${row.format}`}`,
      sortValue: (row) => row.architecture,
    },
    {
      key: 'since',
      header: 'Seen since',
      wideOnly: true,
      cell: (row) => (
        <span title={formatInstant(row.firstSeenAt, { timezone })}>
          {formatRelative(row.firstSeenAt)}
        </span>
      ),
      sortValue: (row) => row.firstSeenAt,
    },
  ];
  return (
    <Card
      title="Packages"
      description={`${data.packages.total.toLocaleString('en-US')} installed${data.packages.newToday > 0 ? `, ${String(data.packages.newToday)} new in the last day` : ''}.${data.scannedAt.packages === null ? '' : ` Last change ${formatRelative(data.scannedAt.packages)}.`}`}
    >
      <TextField
        label="Find a package"
        labelHidden
        placeholder="Find a package or source package"
        value={search}
        maxLength={100}
        onChange={(event) => {
          setSearch(event.target.value.replace(/[^A-Za-z0-9+._:~-]/g, ''));
        }}
      />
      {list.isError ? (
        <Alert tone="danger" title="Could not load the packages">
          {list.error instanceof Error ? list.error.message : 'Try again in a moment.'}
        </Alert>
      ) : (
        <DataTable
          caption="Installed packages"
          columns={columns}
          rows={rows}
          rowKey={(row) => `${row.name}|${row.architecture}|${row.format}`}
          pagination={{
            hasMore: list.hasNextPage,
            loadingMore: list.isFetchingNextPage,
            onLoadMore: () => {
              void list.fetchNextPage();
            },
          }}
          empty={
            <p className="muted text-sm">{list.isPending ? 'Loading…' : 'No package matches.'}</p>
          }
        />
      )}
    </Card>
  );
}

function ProcessesCard({
  processes,
  scannedAt,
}: {
  processes: readonly InventoryProcess[];
  scannedAt: string | null;
}): ReactNode {
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const shown =
    needle === ''
      ? processes
      : processes.filter(
          (each) =>
            each.name.toLowerCase().includes(needle) ||
            (each.command?.toLowerCase().includes(needle) ?? false) ||
            (each.user?.toLowerCase().includes(needle) ?? false),
        );
  const columns: Column<InventoryProcess>[] = [
    {
      key: 'name',
      header: 'Process',
      rowHeader: true,
      cell: (row) => (
        <>
          {row.name}
          {row.command !== null && row.command !== row.name && (
            <span className="cell-sub mono">{row.command}</span>
          )}
        </>
      ),
      sortValue: (row) => row.name,
    },
    {
      key: 'pid',
      header: 'PID',
      align: 'end',
      cell: (row) => row.pid,
      sortValue: (row) => row.pid,
    },
    {
      key: 'user',
      header: 'User',
      cell: (row) => row.user ?? <span className="muted">unknown</span>,
      sortValue: (row) => row.user ?? '',
    },
    {
      key: 'memory',
      header: 'Memory',
      align: 'end',
      cell: (row) => (row.rssBytes === null ? '' : formatBytes(row.rssBytes)),
      sortValue: (row) => row.rssBytes ?? 0,
    },
    {
      key: 'started',
      header: 'Started',
      wideOnly: true,
      cell: (row) => (row.startedAt === null ? '' : formatRelative(row.startedAt)),
      sortValue: (row) => row.startedAt ?? '',
    },
  ];
  return (
    <Card
      title="Processes"
      description={`What ran at the last scan${scannedAt === null ? '' : ` (${formatRelative(scannedAt)})`}, largest first. Their arguments are never read: a command line can hold a password.`}
    >
      <TextField
        label="Find a process"
        labelHidden
        placeholder="Find a process, command or user"
        value={search}
        maxLength={100}
        onChange={(event) => {
          setSearch(event.target.value);
        }}
      />
      <DataTable
        caption="Running processes"
        columns={columns}
        rows={shown}
        rowKey={(row) => String(row.pid)}
        initialSort={{ key: 'memory', direction: 'desc' }}
        empty={<p className="muted text-sm">No process matches.</p>}
      />
    </Card>
  );
}
